import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import ReportTable from "../../common/ReportTable/ReportTable";
import ReportTableRowActions from "../../common/ReportTable/ReportTableRowActions";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import DateRangeFilter from "../../common/DateRangeFilter";
import ConfirmModal from "../../common/ConfirmModal";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import {
  archiveFeasibility,
  fetchFeasibilityList,
  unarchiveFeasibility,
} from "./participantService";
import { useParticipantAccess } from "./participantAccess";
import CreateFeasibilityModal from "./CreateFeasibilityModal";
import FeasibilityCommunicateModal from "./FeasibilityCommunicateModal";
import {
  DEFAULT_FEASIBILITY_COLUMNS,
  FEASIBILITY_COLUMN_DATE_PRESETS,
  FEASIBILITY_DATE_COLUMNS,
  FEASIBILITY_FIELD_OPTIONS,
  PM_ACTIONS_COLUMN,
  PM_DOB_DATE_RANGE_INITIAL,
  PM_PINNED_COLUMNS,
  getFeasibilityCreatedValue,
  getFeasibilityLastEditedValue,
  getFeasibilityRowId,
  getFeasibilityStatusLabel,
  getVolunteerStatusPillStyle,
  isFeasibilityArchived,
  matchesPmDateRange,
  matchTextColumnSearch,
} from "./participantReportUtils";
import "./participantmanager.scss";

const FeasibilityList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { canViewFeasibility, loading: accessLoading } = useParticipantAccess();
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [createdDateRange, setCreatedDateRange] = useState(
    PM_DOB_DATE_RANGE_INITIAL,
  );
  const [lastEditedDateRange, setLastEditedDateRange] = useState(
    PM_DOB_DATE_RANGE_INITIAL,
  );
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [communicateTarget, setCommunicateTarget] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState({
    open: false,
    row: null,
    activate: false,
  });

  const openCreateModal = () => {
    setEditingId("");
    setEditorOpen(true);
  };

  const openEditModal = (participantId) => {
    const id = String(participantId || "").trim();
    if (!id) return;
    setEditingId(id);
    setEditorOpen(true);
  };

  const closeEditorModal = () => {
    setEditorOpen(false);
    setEditingId("");
  };

  const openCommunicateModal = (row) => {
    const id = getFeasibilityRowId(row);
    if (!id) return;
    setCommunicateTarget({
      participantId: id,
      name: String(row?.ParticipantName || "").trim(),
      size: row?.ParticipantSize ?? row?.participantSize ?? "",
    });
  };

  const closeCommunicateModal = () => {
    setCommunicateTarget(null);
  };

  const loadRows = useCallback(async () => {
    if (!userData?.OrganizationId) {
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const { rows: list } = await fetchFeasibilityList(userData);
      setRows(
        (Array.isArray(list) ? list : []).map((row) => ({
          ...row,
          Status: getFeasibilityStatusLabel(row),
        })),
      );
    } catch (err) {
      showAlert({ message: err?.message || "Could not load feasibility list." });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  }, [showAlert, userData]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const editId = String(location.state?.openEditFeasibilityId || "").trim();
    if (editId) {
      openEditModal(editId);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.openCreateFeasibility) {
      openCreateModal();
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    const communicateId = String(
      location.state?.openCommunicateFeasibilityId || "",
    ).trim();
    if (communicateId) {
      setCommunicateTarget({
        participantId: communicateId,
        name: String(location.state?.openCommunicateFeasibilityName || "").trim(),
        size: location.state?.openCommunicateFeasibilitySize ?? "",
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const confirmArchiveChange = async () => {
    const row = archiveConfirm.row;
    const activate = archiveConfirm.activate;
    setArchiveConfirm({ open: false, row: null, activate: false });
    const id = getFeasibilityRowId(row);
    if (!id) return;
    showLoader(activate ? "Activating feasibility..." : "Archiving feasibility...");
    try {
      const result = activate
        ? await unarchiveFeasibility(userData, id)
        : await archiveFeasibility(userData, id);
      showAlert({
        message:
          result?.MSG ||
          result?.Msg ||
          result?.msg ||
          (activate
            ? "Feasibility activated."
            : "Feasibility archived."),
        type: "success",
      });
      await loadRows();
    } catch (err) {
      showAlert({
        message:
          err?.message ||
          (activate
            ? "Could not activate feasibility."
            : "Could not archive feasibility."),
      });
    } finally {
      hideLoader();
    }
  };

  const renderCell = (row, field) => {
    const archived = isFeasibilityArchived(row);
    const id = getFeasibilityRowId(row);

    if (field === "ParticipantName") {
      const label = row?.ParticipantName || "—";
      if (archived || !id) return label;
      return (
        <button
          type="button"
          className="report-table__cell-link pm-campaign-name-link"
          title="Edit feasibility"
          onClick={() => openEditModal(id)}
        >
          {label}
        </button>
      );
    }

    if (field === "ParticipantSize") {
      const size = row?.ParticipantSize ?? row?.participantSize;
      return size === 0 || size ? String(size) : "—";
    }

    if (field === "CreatedDate") {
      return getFeasibilityCreatedValue(row) || "—";
    }

    if (field === "LastEditDateTime") {
      return getFeasibilityLastEditedValue(row) || "—";
    }

    if (field === "Status") {
      const label = getFeasibilityStatusLabel(row);
      const style = getVolunteerStatusPillStyle(
        archived ? "Archived" : "Active",
      );
      return (
        <span className="pm-campaign-status-cell">
          <span
            className="pm-status-pill pm-status-pill--readonly pm-status-pill--square"
            style={style}
          >
            {label}
          </span>
          {archived ? (
            <button
              type="button"
              className="pm-campaign-icon-btn"
              title="Activate feasibility"
              aria-label="Activate feasibility"
              onClick={() =>
                setArchiveConfirm({ open: true, row, activate: true })
              }
            >
              <i className="fas fa-undo" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="pm-campaign-icon-btn pm-campaign-icon-btn--danger"
              title="Archive feasibility"
              aria-label="Archive feasibility"
              onClick={() =>
                setArchiveConfirm({ open: true, row, activate: false })
              }
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
            </button>
          )}
        </span>
      );
    }

    if (field === PM_ACTIONS_COLUMN) {
      if (!id || archived) return "—";
      return (
        <ReportTableRowActions
          items={[
            {
              label: "Communicate",
              icon: "fas fa-envelope",
              onClick: () => openCommunicateModal(row),
            },
          ]}
        />
      );
    }

    return String(row?.[field] ?? "—");
  };

  const visibleRows = useMemo(
    () =>
      (rows || []).filter(
        (row) =>
          matchesPmDateRange(
            getFeasibilityCreatedValue(row),
            createdDateRange,
          ) &&
          matchesPmDateRange(
            getFeasibilityLastEditedValue(row),
            lastEditedDateRange,
          ),
      ),
    [createdDateRange, lastEditedDateRange, rows],
  );

  const matchColumnSearch = useCallback((row, field, query) => {
    if (FEASIBILITY_DATE_COLUMNS.includes(field) || field === PM_ACTIONS_COLUMN) {
      return true;
    }
    return matchTextColumnSearch(row, field, query);
  }, []);

  const renderColumnSearch = useCallback(
    (field, api) => {
      if (api?.checkOnly) {
        return FEASIBILITY_DATE_COLUMNS.includes(field);
      }

      if (field === "CreatedDate") {
        return (
          <DateRangeFilter
            value={createdDateRange}
            onChange={setCreatedDateRange}
            presetOptions={FEASIBILITY_COLUMN_DATE_PRESETS}
            placeholder="Date range"
            compact
            disabled={api?.disabled}
          />
        );
      }

      if (field === "LastEditDateTime") {
        return (
          <DateRangeFilter
            value={lastEditedDateRange}
            onChange={setLastEditedDateRange}
            presetOptions={FEASIBILITY_COLUMN_DATE_PRESETS}
            placeholder="Date range"
            compact
            disabled={api?.disabled}
          />
        );
      }

      return null;
    },
    [createdDateRange, lastEditedDateRange],
  );

  if (accessLoading) {
    return <div className="participant-manager-page pm-page--bootstrapping" />;
  }
  if (!canViewFeasibility) {
    return <Navigate to="/notification-center" replace />;
  }

  const confirmName =
    String(archiveConfirm.row?.ParticipantName ?? "").trim() ||
    "this feasibility";

  return (
    <div className="participant-manager-page pm-list-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Feasibility Manager" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: loadRows,
          },
          {
            icon: "fas fa-plus",
            label: "Create Feasibility",
            onClick: openCreateModal,
          },
        ]}
      />

      <div className="pm-report-wrap">
        <ReportTable
          rows={visibleRows}
          loading={listLoading}
          fieldOptions={FEASIBILITY_FIELD_OPTIONS}
          defaultColumns={DEFAULT_FEASIBILITY_COLUMNS}
          pinnedRightColumns={PM_PINNED_COLUMNS}
          hideAddColumnFields={PM_PINNED_COLUMNS}
          hideColumnHeaderActionsFields={[PM_ACTIONS_COLUMN, "Status"]}
          renderCell={renderCell}
          matchColumnSearch={matchColumnSearch}
          renderColumnSearch={renderColumnSearch}
          persistLayoutKey="pm-feasibility-manager-v3"
          emptyMessage="No feasibility queries found."
        />
      </div>

      <CreateFeasibilityModal
        open={editorOpen}
        participantId={editingId}
        onClose={closeEditorModal}
        onSaved={() => {
          loadRows();
        }}
      />

      <FeasibilityCommunicateModal
        open={Boolean(communicateTarget?.participantId)}
        participantId={communicateTarget?.participantId || ""}
        feasibilityName={communicateTarget?.name || ""}
        participantSize={communicateTarget?.size ?? ""}
        onClose={closeCommunicateModal}
        onSent={() => {
          loadRows();
        }}
      />

      <ConfirmModal
        isOpen={archiveConfirm.open}
        onClose={() =>
          setArchiveConfirm({ open: false, row: null, activate: false })
        }
        onConfirm={confirmArchiveChange}
        title={
          archiveConfirm.activate
            ? "Activate Feasibility"
            : "Archive Feasibility"
        }
        message={
          archiveConfirm.activate
            ? `Are you sure you want to activate "${confirmName}"?`
            : `Are you sure you want to archive "${confirmName}"?`
        }
        confirmText={archiveConfirm.activate ? "Active" : "Archive"}
        cancelText="Cancel"
        confirmButtonClass={
          archiveConfirm.activate ? "btn-success" : "btn-danger"
        }
        icon={
          archiveConfirm.activate
            ? "fas fa-check-circle text-success"
            : "fas fa-archive text-danger"
        }
      />

      <InfoAlert {...alertState} onClose={hideAlert} />
    </div>
  );
};

export default FeasibilityList;
